import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { inr } from "@/lib/api";

const Cart = () => {
  const { items, updateQty, removeItem, total } = useCart();
  const navigate = useNavigate();

  if (items.length === 0)
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-32 text-center">
        <h1 className="font-serif font-light text-5xl md:text-7xl tracking-tight text-[#1A1A1A]">Your bag is empty.</h1>
        <Link to="/shop" data-testid="empty-cart-shop-btn" className="inline-flex items-center gap-3 mt-10 bg-[#1A1A1A] text-[#F9F8F6] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#8C3A3A] transition-colors duration-300">
          Start Shopping <ArrowRight size={16} />
        </Link>
      </div>
    );

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-24 grid md:grid-cols-12 gap-16">
      <div className="md:col-span-8">
        <h1 className="font-serif font-light text-5xl md:text-7xl tracking-tight text-[#1A1A1A] mb-12">Your Bag</h1>
        <div className="divide-y divide-[#E5E1D8] border-t border-[#E5E1D8]">
          {items.map((i) => (
            <div key={`${i.product_id}-${i.size}`} data-testid={`cart-item-${i.product_id}`} className="flex gap-6 py-7">
              <div className="w-24 h-32 bg-[#F2EFE9] overflow-hidden shrink-0">
                <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 flex flex-col justify-between">
                <div className="flex justify-between gap-4">
                  <div>
                    <h3 className="font-serif text-2xl text-[#1A1A1A] leading-tight">{i.name}</h3>
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#595959] mt-1">Size {i.size}</p>
                  </div>
                  <button data-testid={`remove-${i.product_id}`} onClick={() => removeItem(i.product_id, i.size)} className="text-[#595959] hover:text-[#8C3A3A] transition-colors h-fit"><X size={18} /></button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center border border-[#E5E1D8]">
                    <button onClick={() => updateQty(i.product_id, i.size, i.quantity - 1)} className="p-2.5 hover:text-[#8C3A3A] transition-colors"><Minus size={14} /></button>
                    <span className="font-mono text-sm w-8 text-center">{i.quantity}</span>
                    <button onClick={() => updateQty(i.product_id, i.size, i.quantity + 1)} className="p-2.5 hover:text-[#8C3A3A] transition-colors"><Plus size={14} /></button>
                  </div>
                  <p className="font-mono text-sm">{inr(i.price * i.quantity)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="md:col-span-4">
        <div className="border border-[#E5E1D8] p-8 md:sticky md:top-28">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C3A3A] mb-6">Summary</p>
          <div className="space-y-3 font-sans text-sm text-[#595959]">
            <div className="flex justify-between"><span>Subtotal</span><span data-testid="cart-subtotal" className="font-mono text-[#1A1A1A]">{inr(total)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span className="font-mono text-[#1A1A1A]">Free</span></div>
          </div>
          <div className="border-t border-[#E5E1D8] mt-6 pt-6 flex justify-between items-baseline">
            <span className="font-serif text-2xl text-[#1A1A1A]">Total</span>
            <span data-testid="cart-total" className="font-mono text-lg text-[#1A1A1A]">{inr(total)}</span>
          </div>
          <button data-testid="checkout-btn" onClick={() => navigate("/checkout")} className="mt-8 w-full bg-[#1A1A1A] text-[#F9F8F6] py-4 font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-[#8C3A3A] transition-colors duration-300">
            Checkout
          </button>
          <Link to="/shop" className="mt-3 block text-center font-mono text-[11px] uppercase tracking-[0.18em] text-[#595959] hover:text-[#8C3A3A] transition-colors py-2">Continue Shopping</Link>
        </div>
      </div>
    </div>
  );
};

export default Cart;
